import {
  RawVisitHistoryMaterial,
  RawVisitHistoryRouteDay,
  RawVisitHistoryStop,
  RawVisitHistoryStopsResponse,
  RawVisitHistorySummary,
  TiendaPerfectaVisitHistoryDayRoute,
  TiendaPerfectaVisitHistoryExecution,
  TiendaPerfectaVisitHistoryKpis,
  TiendaPerfectaVisitHistoryMaterials,
  TiendaPerfectaVisitHistoryNamed,
  TiendaPerfectaVisitHistoryOutcome,
  TiendaPerfectaVisitHistoryStop,
  TiendaPerfectaVisitHistoryStopsResponse,
  TiendaPerfectaVisitHistorySummary,
  TiendaPerfectaVisitHistoryVanRow,
} from 'models/TiendaPerfecta';

/**
 * Pure adapters from the final backend shape (pass-through by the BFF) onto
 * the internal model the scene consumes. Any field the contract does not
 * guarantee is read defensively.
 */

const named = (
  zone?: { id: string; name?: string } | null
): TiendaPerfectaVisitHistoryNamed => ({
  id: zone?.id ?? '',
  name: zone?.name ?? '',
});

const outcomeOf = (
  stop: RawVisitHistoryStop
): TiendaPerfectaVisitHistoryOutcome => {
  // Only `finished` stops are executed or incidents. `pending`, `in_progress`
  // and any status the contract may add later stay pending: this mirrors the
  // server, which counts every non-finished stop as pending.
  if (stop.status !== 'finished') return 'pending';

  return stop.incident ? 'incident' : 'executed';
};

const adaptStop = (
  stop: RawVisitHistoryStop
): TiendaPerfectaVisitHistoryStop => {
  const hasLocation = stop.lat != null && stop.lng != null;

  return {
    // `routeStopId` is always present; `stopId` is the execution id.
    stopId: stop.routeStopId,
    stopExecutionId: stop.stopId ?? null,
    plannedDate: stop.plannedDate,
    sequence: stop.sequence,
    status: stop.status,
    outcome: outcomeOf(stop),
    incident: stop.incident
      ? {
          code: stop.incident.code,
          reason: stop.incident.reason,
          note: null,
        }
      : null,
    startedAt: stop.startedAt ?? null,
    finishedAt: stop.finishedAt ?? null,
    location: hasLocation
      ? { lat: stop.lat as number, lng: stop.lng as number, accuracyM: null }
      : null,
    route: { id: stop.routeId, code: stop.routeCode },
    zone: named(stop.zone),
    distributor: stop.distributor,
    van: stop.van,
    store: {
      label: stop.local?.label ?? null,
      code: stop.local?.code ?? '',
      branchExternalId: stop.local?.branchExternalId ?? null,
      address: stop.local?.address ?? null,
      name: null,
      city: null,
    },
    materials: (stop.materials ?? []).map((m) => ({
      materialId: m.materialId,
      name: m.name ?? '',
      sku: m.sku ?? null,
      units: m.quantity,
    })),
  };
};

export const adaptVisitHistoryStops = (
  raw?: RawVisitHistoryStopsResponse | null
): TiendaPerfectaVisitHistoryStopsResponse => ({
  context: {
    timezone: raw?.context?.timezone ?? '',
    from: raw?.context?.from ?? '',
    to: raw?.context?.to ?? '',
  },
  stops: (raw?.stops ?? []).map(adaptStop),
});

const adaptKpis = (
  raw: RawVisitHistorySummary['kpis']
): TiendaPerfectaVisitHistoryKpis => ({
  planned: raw?.planned ?? 0,
  executed: raw?.executed ?? 0,
  incidents: raw?.incidents ?? 0,
  pending: raw?.pending ?? 0,
  compliancePct: raw?.compliancePct ?? 0,
  routes: raw?.routes ?? 0,
  materialUnits: raw?.materialUnits ?? 0,
});

const adaptRoutesByDay = (
  rows: RawVisitHistoryRouteDay[]
): TiendaPerfectaVisitHistorySummary['routesByDay'] => {
  const byDate = new Map<string, TiendaPerfectaVisitHistoryDayRoute[]>();

  rows.forEach((row) => {
    const route: TiendaPerfectaVisitHistoryDayRoute = {
      routeId: row.routeId,
      routeCode: row.routeCode,
      zone: named(row.zone),
      distributor: row.distributor,
      van: row.van,
      planned: row.planned ?? 0,
      executed: row.executed ?? 0,
      incidents: row.incidents ?? 0,
    };
    const list = byDate.get(row.date);
    if (list) list.push(route);
    else byDate.set(row.date, [route]);
  });

  return [...byDate.entries()].map(([date, routes]) => ({ date, routes }));
};

/**
 * `routes` of a day: the backend's own count when it sends one, otherwise the
 * number of route rows of that date in `routesByDay` (the real `byDay[]` has
 * none). The model always carries a number; whether a day is empty is decided
 * by `planned`, never by this count.
 */
const adaptByDay = (
  days: RawVisitHistorySummary['byDay'],
  routeRows: RawVisitHistoryRouteDay[]
): TiendaPerfectaVisitHistorySummary['byDay'] => {
  const counts = new Map<string, number>();
  routeRows.forEach((r) => counts.set(r.date, (counts.get(r.date) ?? 0) + 1));

  return (days ?? []).map((day) => ({
    ...day,
    routes: day.routes ?? counts.get(day.date) ?? 0,
  }));
};

const adaptMaterials = (
  raw: RawVisitHistorySummary['materials']
): TiendaPerfectaVisitHistoryMaterials => {
  const totals = raw?.totals ?? [];
  const byDay = raw?.byDay ?? [];
  const byVan = raw?.byVan ?? [];
  const byZone = raw?.byZone ?? [];

  const catalog = new Map<
    string,
    TiendaPerfectaVisitHistoryMaterials['catalog'][number]
  >();
  const register = (row: RawVisitHistoryMaterial) => {
    if (catalog.has(row.materialId)) return;
    catalog.set(row.materialId, {
      materialId: row.materialId,
      sku: row.sku ?? '',
      name: row.name ?? '',
    });
  };
  [...totals, ...byDay, ...byVan, ...byZone].forEach(register);

  return {
    catalog: [...catalog.values()],
    totals: totals.map((r) => ({
      materialId: r.materialId,
      units: r.quantity,
    })),
    byDay: byDay.map((r) => ({
      date: r.date,
      materialId: r.materialId,
      units: r.quantity,
    })),
    byVan: byVan.map((r) => ({
      vanId: r.van.id,
      materialId: r.materialId,
      units: r.quantity,
    })),
    byZone: byZone.map((r) => ({
      zoneId: r.zone.id,
      zoneName: r.zone.name ?? '',
      materialId: r.materialId,
      units: r.quantity,
    })),
  };
};

const adaptVans = (
  raw: RawVisitHistorySummary['vans'],
  routesByDay: RawVisitHistoryRouteDay[]
): TiendaPerfectaVisitHistoryVanRow[] => {
  const routeDay = new Map(
    routesByDay.map((r) => [`${r.routeId}|${r.date}`, r])
  );

  return (raw ?? []).map((row): TiendaPerfectaVisitHistoryVanRow => {
    // Van zones: `row.zones` (id + name) when the backend sends them, else the
    // zones of the van's distributors. Deduplicated by id; empty when neither
    // is present.
    const zones = new Map<string, TiendaPerfectaVisitHistoryNamed>();
    (row.zones ?? []).forEach((z) => {
      if (z?.id && !zones.has(z.id)) zones.set(z.id, named(z));
    });
    (row.distributors ?? []).forEach((d) => {
      if (d.zone?.id && !zones.has(d.zone.id)) {
        zones.set(d.zone.id, named(d.zone));
      }
    });

    return {
      van: row.van,
      zones: [...zones.values()],
      routes: row.routes ?? 0,
      days: row.days ?? 0,
      planned: row.planned ?? 0,
      executed: row.executed ?? 0,
      incidents: row.incidents ?? 0,
      pending: row.pending ?? 0,
      compliancePct: row.compliancePct ?? 0,
      materialUnits: row.materialUnits ?? 0,
      avgStartTime: row.avgStartTime ?? null,
      avgEndTime: row.avgEndTime ?? null,
      avgDurationMinutes: row.avgDurationMinutes ?? null,
      visitsPerHour: row.visitsPerHour ?? null,
      executions: (row.executions ?? []).map(
        (e): TiendaPerfectaVisitHistoryExecution => {
          const day = routeDay.get(`${e.routeId}|${e.plannedDate}`);

          return {
            routeId: e.routeId,
            routeCode: e.routeCode,
            date: e.plannedDate,
            zone: named(day?.zone),
            planned: day?.planned ?? 0,
            executed: day?.executed ?? 0,
            incidents: day?.incidents ?? 0,
            pending: day?.pending ?? 0,
            // Not in the contract per execution: null, never an invented 0.
            compliancePct: null,
            materialUnits: null,
            startedAt: e.startedAt ?? null,
            finishedAt: e.finishedAt ?? null,
            durationMinutes: e.durationMinutes ?? null,
            visitsPerHour: null,
          };
        }
      ),
    };
  });
};

export const adaptVisitHistorySummary = (
  raw?: RawVisitHistorySummary | null
): TiendaPerfectaVisitHistorySummary => {
  const routes = raw?.routesByDay ?? [];

  return {
    context: {
      timezone: raw?.context?.timezone ?? '',
      from: raw?.context?.from ?? '',
      to: raw?.context?.to ?? '',
    },
    kpis: adaptKpis(raw?.kpis),
    excludedRoutes: raw?.kpis?.excludedRoutes ?? 0,
    byDay: adaptByDay(raw?.byDay ?? [], routes),
    routesByDay: adaptRoutesByDay(routes),
    materials: adaptMaterials(raw?.materials),
    vans: adaptVans(raw?.vans, routes),
  };
};
